import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch } from '@/hooks/useRedux'
import { login, setRefresh, setToken } from '@/pages/auth/authSlice'
import { updateUser } from '@/pages/user/userSlice'
import { useVerifyTwoFactor, useVerifyWebAuthn } from '@/services'
import { COOKIE_SESSION } from '@/constants/Common'
import { findTwoFactorChallenge, requirementsFrom, SECOND_FACTOR_URN, SecondFactorKind } from '@/types'
import { isWebAuthnSupported } from '@/utils/webauthn'
import { createSignInValidationSchema } from '@/utils/schema/SignInValidationSchema'
import { AppButton, AppTextField } from '@/pages/external/components/shell'

type FormValues = { email: string; password: string }

interface OAuthLoginFormProps {
  partnerName: string
  /** Called once the DUPR first-party session cookie is established (post-2FA). No token crosses JS. */
  onAuthenticated: () => void
  /** Optional "use my existing session instead" affordance shown above the form. */
  onBack?: () => void
}

/**
 * DUPR login form for the OAuth consent flow, rendered ON DUPR'S OWN DOMAIN. It runs the exact same
 * path as the main app: password → mandatory email 2FA → an HttpOnly session cookie. It never holds
 * a token; once the cookie is set it calls `onAuthenticated()`, and the flow's bind/mint calls
 * authenticate by sending that cookie (withCredentials). Credentials are posted only to DUPR.
 */
export const OAuthLoginForm: React.FC<OAuthLoginFormProps> = ({ partnerName, onAuthenticated, onBack }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // The 2FA challenge, present once password auth returns the 428.
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [methods, setMethods] = useState<SecondFactorKind[]>([])
  const [usePasskey, setUsePasskey] = useState(false)
  const [code, setCode] = useState('')
  const { mutate: verifyTwoFactor, isLoading: isVerifying } = useVerifyTwoFactor()
  const { mutate: verifyWebAuthn, isLoading: isVerifyingPasskey } = useVerifyWebAuthn()

  const has = (urn: string) => methods.some((m) => m.type === urn)
  const passkeyOffered = has(SECOND_FACTOR_URN.WEBAUTHN) && isWebAuthnSupported()
  const isTotp = has(SECOND_FACTOR_URN.TOTP)
  const hasCode = isTotp || has(SECOND_FACTOR_URN.EMAIL) || has(SECOND_FACTOR_URN.SMS)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(createSignInValidationSchema(t)),
  })

  const onSubmit = handleSubmit(async (data) => {
    setError(null)
    setSubmitting(true)
    try {
      await dispatch(login({ email: data.email.trim().toLocaleLowerCase(), password: data.password })).unwrap()
      // Login never returns a session directly now; it answers a 428 (handled in catch).
    } catch (err: unknown) {
      // Gate on the rich two-factor-challenge requirement (URN-tagged), not the legacy
      // `requiredActions`/`challengeToken` fields. Both the token and the methods come from it —
      // strong factors (TOTP/passkey) only ever appeared here. The SSO path enforces the same
      // strong-MFA policy as the main app.
      const challengeReq = findTwoFactorChallenge(requirementsFrom(err))
      if (challengeReq) {
        const advertised: SecondFactorKind[] = challengeReq.methods ?? [{ type: SECOND_FACTOR_URN.EMAIL }]
        const onlyPasskey =
          advertised.some((m) => m.type === SECOND_FACTOR_URN.WEBAUTHN) &&
          isWebAuthnSupported() &&
          !advertised.some(
            (m) =>
              m.type === SECOND_FACTOR_URN.TOTP ||
              m.type === SECOND_FACTOR_URN.EMAIL ||
              m.type === SECOND_FACTOR_URN.SMS
          )
        setMethods(advertised)
        setUsePasskey(onlyPasskey)
        setChallengeToken(challengeReq.challengeToken)
      } else {
        const message = (err as { message?: string })?.message
        setError(message || 'We could not sign you in. Check your details and try again.')
      }
    } finally {
      setSubmitting(false)
    }
  })

  const completeAuth = (data: any) => {
    // A 2xx means the session cookie is set. Record the marker + user, then let the flow bind.
    dispatch(setToken(COOKIE_SESSION))
    dispatch(setRefresh(COOKIE_SESSION))
    if (data?.result?.user) dispatch(updateUser(data.result.user))
    onAuthenticated()
  }

  const onVerify = () => {
    if (!challengeToken || code.length !== 6) return
    setError(null)
    verifyTwoFactor(
      { challengeToken, code, method: isTotp ? { type: SECOND_FACTOR_URN.TOTP } : undefined },
      {
        onSuccess: completeAuth,
        onError: () => setError(t('auth.twoFactor.invalidCode', 'That code was not accepted. Try again.')),
      }
    )
  }

  const onVerifyPasskey = () => {
    if (!challengeToken) return
    setError(null)
    verifyWebAuthn(
      { challengeToken },
      {
        onSuccess: completeAuth,
        onError: () => setError(t('twoFactor.passkeyFailed', 'Passkey sign-in failed. Try again.')),
      }
    )
  }

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault()
        if (challengeToken) {
          if (usePasskey) onVerifyPasskey()
          else onVerify()
        } else onSubmit(ev)
      }}
      className="flex flex-col w-full gap-y-5"
    >
      <div className="text-center">
        <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)] mb-2">Sign in to DUPR</p>
        <h1 className="text-2xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
          Connect to {partnerName}
        </h1>
      </div>

      {error && (
        <p
          className="text-sm text-[color:var(--negative)] bg-[color:var(--negative)]/8 border border-[color:var(--negative)]/20 rounded-[var(--radius-input)] px-3 py-2"
          role="alert"
        >
          {error}
        </p>
      )}

      {!challengeToken ? (
        <>
          <AppTextField
            label={t('common.email')}
            placeholder={t('common.emailPlaceHolder')}
            error={errors.email?.message}
            variant="email"
            autoComplete="email"
            {...register('email')}
          />

          <AppTextField
            label={t('common.password')}
            placeholder={t('common.passwordPlaceHolder')}
            error={errors.password?.message}
            variant="password"
            autoComplete="current-password"
            {...register('password')}
          />

          <div className="flex flex-col items-stretch gap-y-2 mt-1">
            <AppButton type="submit" fullWidth loading={submitting} disabled={submitting}>
              {submitting ? t('common.logging') : t('common.login')}
            </AppButton>
            {onBack && (
              <AppButton type="button" variant="ghost" fullWidth onClick={onBack} disabled={submitting}>
                Back
              </AppButton>
            )}
          </div>
        </>
      ) : usePasskey ? (
        <>
          <p className="text-sm text-[color:var(--ink-soft)] text-center">
            {t('twoFactor.usePasskeySubtitle', 'Use your device fingerprint, face, or screen lock to sign in.')}
          </p>
          <div className="flex flex-col items-stretch gap-y-2 mt-1">
            <AppButton type="submit" fullWidth loading={isVerifyingPasskey} disabled={isVerifyingPasskey}>
              {isVerifyingPasskey ? t('common.loading', 'Loading…') : t('twoFactor.usePasskey', 'Use a passkey')}
            </AppButton>
            {hasCode && (
              <AppButton type="button" variant="ghost" fullWidth onClick={() => setUsePasskey(false)}>
                {t('twoFactor.useCodeInstead', 'Use a code instead')}
              </AppButton>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[color:var(--ink-soft)] text-center">
            {isTotp
              ? t('twoFactor.authSubtitle', 'Enter the 6-digit code from your authenticator app.')
              : t('auth.twoFactor.enterCode', 'Enter the 6-digit code we emailed you.')}
          </p>
          <AppTextField
            label={t('auth.twoFactor.code', 'Verification code')}
            placeholder="••••••"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <div className="flex flex-col items-stretch gap-y-2 mt-1">
            <AppButton type="submit" fullWidth loading={isVerifying} disabled={isVerifying || code.length !== 6}>
              {isVerifying ? t('common.loading', 'Loading…') : t('auth.twoFactor.verify', 'Verify')}
            </AppButton>
            {passkeyOffered && (
              <AppButton type="button" variant="ghost" fullWidth onClick={() => setUsePasskey(true)}>
                {t('twoFactor.usePasskeyInstead', 'Use a passkey instead')}
              </AppButton>
            )}
          </div>
        </>
      )}
    </form>
  )
}

export default OAuthLoginForm
